<script setup lang="ts">
/**
 * The nothing-here state. These are the screens a founder sees on day one, so they are written as
 * an instruction rather than an apology — BG §11's voice: direct, operational, never cute.
 *
 * One of the few product surfaces that earns the backdrop: there is no data to compete with it.
 *
 * `compact` is the same statement made inside data — a table cell, a line of a list — where the backdrop
 * must not sit (ZhBackdrop: never behind tables) and a full block would push the data apart. It drops the
 * backdrop and the padding and reads on one line: the kicker, then the words.
 */
import ZhBackdrop from './ZhBackdrop.vue'

withDefaults(
    defineProps<{
        /**
         * The mono kicker: `NO RUNS`, `NO GATES`, `TEAM EMPTY`. Optional, so a screen moving onto the kit
         * can keep the copy it already ships rather than gain a kicker it never had.
         */
        label?: string
        /** One line, no backdrop: for an empty state that sits inside a table cell or a dense list. */
        compact?: boolean
    }>(),
    { compact: false },
)
</script>

<template>
    <div class="zh-empty-state" :data-compact="compact ? 'on' : 'off'">
        <ZhBackdrop v-if="!compact" intensity="chrome" />
        <div class="zh-empty-state__body">
            <p v-if="label" class="zh-empty-state__label">{{ label }}</p>
            <p class="zh-empty-state__copy"><slot /></p>
            <div v-if="$slots.action" class="zh-empty-state__action"><slot name="action" /></div>
        </div>
    </div>
</template>

<style scoped>
.zh-empty-state {
    position: relative;
    border: 1px solid var(--border);
    padding: 2.5rem 1.25rem;
}

.zh-empty-state__body {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    text-align: center;
}

.zh-empty-state__label {
    margin: 0;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    color: var(--electric-cyan);
}

.zh-empty-state__copy {
    margin: 0;
    max-width: 30rem;
    font-family: var(--font-body);
    font-size: 0.875rem;
    line-height: 1.55;
    color: color-mix(in srgb, var(--dirty-white) 80%, transparent);
    text-wrap: pretty;
}

.zh-empty-state__action {
    margin-top: 0.5rem;
}

/* Inside data: a dashed hairline says "nothing here" without drawing a second panel in the cell. */
.zh-empty-state[data-compact='on'] {
    border-style: dashed;
    padding: 0.125rem 0.4375rem;
}

.zh-empty-state[data-compact='on'] .zh-empty-state__body {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.5rem;
    text-align: left;
}

.zh-empty-state[data-compact='on'] .zh-empty-state__copy {
    font-size: 0.75rem;
}

.zh-empty-state[data-compact='on'] .zh-empty-state__action {
    margin-top: 0;
}
</style>
